import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@core/button";
import { Text } from "@core/text";
import {
  ClipboardCheck,
  Eye,
  Sparkles,
  Plus,
  X,
  Lightbulb,
  AlertTriangle,
} from "lucide-react";
import {
  GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  LearningOutcomeDto,
} from "@packages/api/madlenAPI.schemas";

// Types
interface UbdAssessmentExperiencesStepProps {
  planId?: number;
  ubdPerformanceTasks: string[];
  ubdOtherEvidence: string[];
  ubdUnderstandingGoals?: string[];
  ubdTransferGoals?: string[];
  learningEvidence?: string; // TYMM learning evidence data
  learningOutcomes?: LearningOutcomeDto[];
  selectedMaarifData?: {
    contentFramework?: string;
    keyConcepts?: string;
    fieldSkills?: string[];
    conceptualSkills?: string[];
    trends?: string[];
    socialEmotionalLearning?: string[];
    values?: string[];
    literacySkills?: string[];
    interdisciplinaryConnections?: string[];
    crossCurricularConnections?: string[];
  };
  isLoading: boolean;
  onGenerateWithAI: (
    generationType: GenerateUbdGoalsSubstepWithAiRequestDtoGenerationType,
  ) => void;
  onFieldChange: (field: string, value: string[]) => void;
}

const UbdAssessmentExperiencesStep: React.FC<
  UbdAssessmentExperiencesStepProps
> = ({
  planId,
  ubdPerformanceTasks,
  ubdOtherEvidence,
  ubdUnderstandingGoals = [],
  ubdTransferGoals = [],
  learningEvidence = "",
  learningOutcomes = [],
  selectedMaarifData = {},
  isLoading,
  onGenerateWithAI,
  onFieldChange,
}) => {
  // Initialize arrays with proper split - simplified approach
  const initializePerformanceTasks = useCallback(
    (contentArray: string[]): string[] => {
      if (!contentArray || contentArray.length === 0) {
        return ["", "", ""]; // 3 cards for performance tasks (as shown in UI mockup)
      }
      return contentArray.map((content) => content.trim());
    },
    [],
  );

  const initializeOtherEvidence = useCallback(
    (contentArray: string[]): string[] => {
      if (!contentArray || contentArray.length === 0) {
        return ["", "", ""]; // 3 cards for other evidence
      }
      return contentArray.map((content) => content.trim());
    },
    [],
  );

  // Initialize arrays - simplified state management
  const [performanceTasks, setPerformanceTasks] = useState<string[]>(() =>
    initializePerformanceTasks(ubdPerformanceTasks),
  );
  const [otherEvidence, setOtherEvidence] = useState<string[]>(() =>
    initializeOtherEvidence(ubdOtherEvidence),
  );

  // Popover state for warning
  const [showWarningPopover, setShowWarningPopover] = useState(false);

  // Check if there's existing data
  const hasExistingData = useCallback(() => {
    const hasPerformanceTasks = performanceTasks.some(
      (task) => task.trim() !== "",
    );
    const hasOtherEvidence = otherEvidence.some(
      (evidence) => evidence.trim() !== "",
    );

    return hasPerformanceTasks || hasOtherEvidence;
  }, [performanceTasks, otherEvidence]);

  useEffect(() => {
    if (ubdPerformanceTasks.length > 0 || ubdOtherEvidence.length > 0) {
      if (performanceTasks.toString() !== ubdPerformanceTasks.toString()) {
        setPerformanceTasks(initializePerformanceTasks(ubdPerformanceTasks));
      }
      if (otherEvidence.toString() !== ubdOtherEvidence.toString()) {
        setOtherEvidence(initializeOtherEvidence(ubdOtherEvidence));
      }
    }
  }, [
    ubdPerformanceTasks,
    ubdOtherEvidence,
    initializePerformanceTasks,
    initializeOtherEvidence,
  ]);

  // Track loading states
  const [loadingStates, setLoadingStates] = useState({
    performanceTasks: false,
    otherEvidence: false,
  });

  // Update loading states when parent loading state changes
  useEffect(() => {
    if (!isLoading) {
      setLoadingStates({
        performanceTasks: false,
        otherEvidence: false,
      });
    }
  }, [isLoading]);

  // Array update functions - simplified
  const updatePerformanceTask = useCallback(
    (index: number, content: string) => {
      setPerformanceTasks((prev) => {
        const updated = [...prev];
        updated[index] = content;
        onFieldChange("ubdPerformanceTasks", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const updateOtherEvidence = useCallback(
    (index: number, content: string) => {
      setOtherEvidence((prev) => {
        const updated = [...prev];
        updated[index] = content;
        onFieldChange("ubdOtherEvidence", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  // Add functions - simplified
  const addPerformanceTask = useCallback(() => {
    setPerformanceTasks((prev) => {
      const updated = [...prev, ""];
      onFieldChange("ubdPerformanceTasks", updated);
      return updated;
    });
  }, [onFieldChange]);

  const addOtherEvidence = useCallback(() => {
    setOtherEvidence((prev) => {
      const updated = [...prev, ""];
      onFieldChange("ubdOtherEvidence", updated);
      return updated;
    });
  }, [onFieldChange]);

  // Remove functions - simplified
  const removePerformanceTask = useCallback(
    (index: number) => {
      setPerformanceTasks((prev) => {
        if (prev.length <= 1) return prev;
        const updated = prev.filter((_, i) => i !== index);
        onFieldChange("ubdPerformanceTasks", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  const removeOtherEvidence = useCallback(
    (index: number) => {
      setOtherEvidence((prev) => {
        if (prev.length <= 1) return prev;
        const updated = prev.filter((_, i) => i !== index);
        onFieldChange("ubdOtherEvidence", updated);
        return updated;
      });
    },
    [onFieldChange],
  );

  // AI GENERATION FUNCTIONS - updated to use modal system
  const generatePerformanceTasksAI = useCallback(() => {
    if (!planId) {
      console.error("Plan ID is required for AI generation");
      return;
    }

    onGenerateWithAI("PERFORMANCE_TASKS");
  }, [planId, onGenerateWithAI]);

  const generateOtherEvidenceAI = useCallback(() => {
    if (!planId) {
      console.error("Plan ID is required for AI generation");
      return;
    }

    onGenerateWithAI("OTHER_EVIDENCE");
  }, [planId, onGenerateWithAI]);

  // Generate all sections with AI
  const handleGenerateAll = useCallback(() => {
    if (!planId) {
      console.error("Plan ID is required for AI generation");
      return;
    }

    if (hasExistingData()) {
      setShowWarningPopover(true);
    } else {
      setLoadingStates({
        performanceTasks: true,
        otherEvidence: true,
      });

      onGenerateWithAI("ASSESSMENT_AND_EXPERIENCES");
    }
  }, [planId, onGenerateWithAI, hasExistingData]);

  // Confirm generate all
  const confirmGenerateAll = () => {
    setShowWarningPopover(false);
    setLoadingStates({
      performanceTasks: true,
      otherEvidence: true,
    });
    onGenerateWithAI("ASSESSMENT_AND_EXPERIENCES");
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showWarningPopover && event.target instanceof Element) {
        const popover = document.querySelector(".warning-popover");
        const button = document.querySelector(".generate-all-button");

        if (
          popover &&
          !popover.contains(event.target) &&
          button &&
          !button.contains(event.target)
        ) {
          setShowWarningPopover(false);
        }
      }
    };

    if (showWarningPopover) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showWarningPopover]);

  // Check if any section is loading
  const isAnyLoading =
    loadingStates.performanceTasks || loadingStates.otherEvidence || isLoading;

  // Auto resize function for textareas
  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = "auto";
    const newHeight = Math.min(Math.max(textarea.scrollHeight, 40), 80);
    textarea.style.height = newHeight + "px";
  };

  // Auto-resize all textareas when data changes
  useEffect(() => {
    const resizeAllTextareas = () => {
      const textareas = document.querySelectorAll("textarea");
      textareas.forEach((textarea) => {
        if (textarea.value) {
          autoResize(textarea as HTMLTextAreaElement);
        }
      });
    };

    // Small delay to ensure DOM is updated
    const timer = setTimeout(resizeAllTextareas, 0);

    return () => clearTimeout(timer);
  }, [performanceTasks, otherEvidence]);

  // Render Section Cards Function - simplified to use array indices
  const renderSectionCards = (
    items: string[],
    updateItem: (index: number, content: string) => void,
    addItem: () => void,
    removeItem: (index: number) => void,
    generateAI: () => void,
    config: {
      title: string;
      icon: React.ReactNode;
      placeholder: string;
      description: string;
      isLoading: boolean;
      colorClasses: {
        iconColor: string;
        cardBg: string;
        cardBorder: string;
        focusRing: string;
        aiButton: string;
        addButtonHover: string;
      };
    },
  ) => (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div
            className={`p-2 rounded-lg ${config.colorClasses.iconColor} shadow-sm`}
          >
            {config.icon}
          </div>
          <div>
            <Text
              variant="h3"
              className="text-lg font-bold text-gray-900 dark:text-gray-100"
            >
              {config.title}
            </Text>
            <Text
              variant="p"
              size="sm"
              className="text-gray-600 dark:text-gray-400 max-w-xs"
            >
              {config.description}
            </Text>
          </div>
        </div>

        {/* AI Generate Button */}
        <Button
          variant="solid"
          size="sm"
          onClick={generateAI}
          disabled={config.isLoading || !planId}
          className={`px-3 py-2 text-white text-xs rounded-lg transition-all duration-300 disabled:opacity-50 hover:scale-105 transform hover:shadow-lg font-medium ${config.colorClasses.aiButton}`}
        >
          {config.isLoading ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent mr-2"></div>
              <span className="animate-pulse">...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-3 h-3 mr-2" />
              AI
            </>
          )}
        </Button>
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {items.map((item, index) => (
          <div
            key={index}
            className={`relative group ${config.colorClasses.cardBg} ${config.colorClasses.cardBorder} rounded-xl pl-2 shadow-sm hover:shadow-md transition-all duration-300`}
          >
            {/* Card Number */}
            <div className="absolute -top-2 -left-2 w-6 h-6 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-400 shadow-sm">
              {index + 1}
            </div>

            {/* Remove Button */}
            {items.length > 1 && (
              <button
                onClick={() => removeItem(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-sm hover:scale-110"
              >
                <X className="w-3 h-3" />
              </button>
            )}

            {/* Content Textarea */}
            <textarea
              value={item}
              onChange={(e) => {
                updateItem(index, e.target.value);
                autoResize(e.target);
              }}
              onInput={(e) => autoResize(e.target as HTMLTextAreaElement)}
              placeholder={config.placeholder}
              disabled={isAnyLoading}
              className={`w-full bg-transparent border-0 focus:outline-none focus:ring-0 resize-none text-sm text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 ${config.colorClasses.focusRing} rounded-lg p-2 min-h-[40px] ${isAnyLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              style={{ height: "auto" }}
              rows={1}
            />
          </div>
        ))}

        {/* Add New Card Button */}
        <button
          type="button"
          onClick={addItem}
          disabled={isAnyLoading}
          className={`w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl transition-all duration-300 ${config.colorClasses.addButtonHover} group disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]`}
        >
          <div className="flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300">
            <Plus className="w-4 h-4" />
            <Text variant="span" size="sm" className="font-medium">
              Yeni Ekle
            </Text>
          </div>
        </button>
      </div>
    </div>
  );

  // Cleanup function for timeouts - simplified (no timeouts to clear)
  useEffect(() => {
    return () => {
      // No cleanup needed for simplified state management
    };
  }, []);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* TYMM Learning Evidence Section - Top Gray Box */}
      {learningEvidence && (
        <div className="relative group">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-400/20 to-slate-400/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-300" />
          <div className="relative bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-800 dark:via-slate-800 dark:to-gray-700 rounded-3xl p-8 border border-gray-200/60 dark:border-gray-600/60 shadow-xl backdrop-blur-sm">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-3 bg-white/80 dark:bg-gray-800/80 px-6 py-3 rounded-2xl shadow-lg backdrop-blur-sm border border-gray-200/50 dark:border-gray-600/50">
                <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-slate-600 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <Text
                  variant="h3"
                  className="text-lg font-bold bg-gradient-to-r from-gray-700 to-slate-700 dark:from-gray-200 dark:to-slate-200 bg-clip-text text-transparent"
                >
                  TYMM'den gelen Öğrenme Kanıtları (Ölçme ve Değerlendirme)
                </Text>
              </div>
            </div>
            <div className="bg-white/90 dark:bg-gray-900/90 rounded-2xl p-6 shadow-lg backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
              <Text
                variant="p"
                size="sm"
                className="text-gray-700 dark:text-gray-300 leading-relaxed"
              >
                {learningEvidence}
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* TYMM Learning Outcomes - Full Width Beautiful Card */}
      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-400/30 via-indigo-400/30 to-purple-400/30 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />
        <div className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-3xl p-8 border border-blue-200/60 dark:border-blue-700/60 shadow-2xl backdrop-blur-sm overflow-hidden">
          {/* Decorative Background Pattern */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-400/10 to-blue-400/10 rounded-full blur-2xl" />

          <div className="relative">
            {/* Header - Integrated with content */}
            <div className="bg-white/95 dark:bg-blue-900/30 rounded-2xl shadow-xl backdrop-blur-sm border border-blue-200/50 dark:border-blue-700/50 overflow-hidden">
              {/* Header Section */}
              <div className="bg-gradient-to-r from-blue-50/90 via-indigo-50/90 to-purple-50/90 dark:from-blue-800/40 dark:via-indigo-800/40 dark:to-purple-800/40 px-6 py-5 border-b border-blue-200/60 dark:border-blue-600/60">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  </div>
                  <Text
                    variant="h2"
                    className="text-xl font-bold bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 dark:from-blue-200 dark:via-indigo-200 dark:to-purple-200 bg-clip-text text-transparent"
                  >
                    TYMM'den Öğrenme Çıktıları ve Süreç Bileşenleri
                  </Text>
                </div>
              </div>

              {/* Content Section */}
              <div className="p-6 space-y-6">
                {/* Learning Outcomes */}
                {learningOutcomes.length > 0 && (
                  <div className="bg-gradient-to-r from-emerald-50/60 to-teal-50/60 dark:from-emerald-900/15 dark:to-teal-900/15 rounded-xl p-5 border border-emerald-200/40 dark:border-emerald-700/40">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                      <Text
                        variant="h3"
                        className="text-lg font-bold text-emerald-800 dark:text-emerald-200"
                      >
                        Öğrenme Çıktıları
                      </Text>
                    </div>
                    <div className="grid gap-3">
                      {learningOutcomes.map((outcome, index) => (
                        <div
                          key={outcome.id || index}
                          className="group bg-white/80 dark:bg-emerald-900/20 rounded-lg p-4 border border-emerald-200/50 dark:border-emerald-700/50 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-5 h-5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-md flex items-center justify-center shadow-sm flex-shrink-0 mt-0.5">
                              <Text
                                variant="p"
                                size="xs"
                                className="text-white font-bold"
                              >
                                {index + 1}
                              </Text>
                            </div>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-emerald-800 dark:text-emerald-200 leading-relaxed"
                            >
                              {outcome.text}
                            </Text>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Other Maarif Data Sections */}
                {(selectedMaarifData.contentFramework ||
                  selectedMaarifData.keyConcepts) && (
                  <div className="bg-gradient-to-r from-slate-50/60 to-gray-50/60 dark:from-slate-900/15 dark:to-gray-900/15 rounded-xl p-5 border border-slate-200/40 dark:border-slate-700/40">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Content Framework */}
                      {selectedMaarifData.contentFramework && (
                        <div className="bg-white/80 dark:bg-violet-900/15 rounded-lg p-4 border border-violet-200/50 dark:border-violet-700/50 shadow-sm">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-7 h-7 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg
                                className="w-3.5 h-3.5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                              </svg>
                            </div>
                            <Text
                              variant="h4"
                              className="text-base font-semibold text-violet-800 dark:text-violet-200"
                            >
                              İçerik Çerçevesi
                            </Text>
                          </div>
                          <Text
                            variant="p"
                            size="sm"
                            className="text-violet-700 dark:text-violet-300 leading-relaxed"
                          >
                            {selectedMaarifData.contentFramework}
                          </Text>
                        </div>
                      )}

                      {/* Key Concepts */}
                      {selectedMaarifData.keyConcepts && (
                        <div className="bg-white/80 dark:bg-amber-900/15 rounded-lg p-4 border border-amber-200/50 dark:border-amber-700/50 shadow-sm">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-7 h-7 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center shadow-md">
                              <svg
                                className="w-3.5 h-3.5 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                />
                              </svg>
                            </div>
                            <Text
                              variant="h4"
                              className="text-base font-semibold text-amber-800 dark:text-amber-200"
                            >
                              Anahtar Kavramlar
                            </Text>
                          </div>
                          <Text
                            variant="p"
                            size="sm"
                            className="text-amber-700 dark:text-amber-300 leading-relaxed"
                          >
                            {selectedMaarifData.keyConcepts}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Cross-Curricular Components */}
                {Object.entries(selectedMaarifData).some(
                  ([key, value]) =>
                    [
                      "fieldSkills",
                      "conceptualSkills",
                      "trends",
                      "socialEmotionalLearning",
                      "values",
                      "literacySkills",
                      "interdisciplinaryConnections",
                      "crossCurricularConnections",
                    ].includes(key) &&
                    Array.isArray(value) &&
                    value.length > 0,
                ) && (
                  <div className="bg-gradient-to-r from-rose-50/60 to-pink-50/60 dark:from-rose-900/15 dark:to-pink-900/15 rounded-xl p-5 border border-rose-200/40 dark:border-rose-700/40">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-gradient-to-br from-rose-500 to-pink-600 rounded-lg flex items-center justify-center shadow-md">
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                          />
                        </svg>
                      </div>
                      <Text
                        variant="h3"
                        className="text-lg font-bold text-rose-800 dark:text-rose-200"
                      >
                        Programlar Arası Bileşenler
                      </Text>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {selectedMaarifData.fieldSkills &&
                        selectedMaarifData.fieldSkills.length > 0 && (
                          <div className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 rounded-xl p-4 border border-rose-200/60 dark:border-rose-700/60 shadow-md hover:shadow-lg transition-all duration-300">
                            <Text
                              variant="p"
                              size="xs"
                              className="text-rose-600 dark:text-rose-400 font-bold mb-2 uppercase tracking-wide"
                            >
                              Alan Becerileri
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-rose-800 dark:text-rose-200"
                            >
                              {selectedMaarifData.fieldSkills.join(", ")}
                            </Text>
                          </div>
                        )}
                      {selectedMaarifData.conceptualSkills &&
                        selectedMaarifData.conceptualSkills.length > 0 && (
                          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 dark:from-cyan-900/20 dark:to-teal-900/20 rounded-xl p-4 border border-cyan-200/60 dark:border-cyan-700/60 shadow-md hover:shadow-lg transition-all duration-300">
                            <Text
                              variant="p"
                              size="xs"
                              className="text-cyan-600 dark:text-cyan-400 font-bold mb-2 uppercase tracking-wide"
                            >
                              Kavramsal Beceriler
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-cyan-800 dark:text-cyan-200"
                            >
                              {selectedMaarifData.conceptualSkills.join(", ")}
                            </Text>
                          </div>
                        )}
                      {selectedMaarifData.values &&
                        selectedMaarifData.values.length > 0 && (
                          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl p-4 border border-indigo-200/60 dark:border-indigo-700/60 shadow-md hover:shadow-lg transition-all duration-300">
                            <Text
                              variant="p"
                              size="xs"
                              className="text-indigo-600 dark:text-indigo-400 font-bold mb-2 uppercase tracking-wide"
                            >
                              Değerler
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-indigo-800 dark:text-indigo-200"
                            >
                              {selectedMaarifData.values.join(", ")}
                            </Text>
                          </div>
                        )}
                      {selectedMaarifData.socialEmotionalLearning &&
                        selectedMaarifData.socialEmotionalLearning.length >
                          0 && (
                          <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl p-4 border border-purple-200/60 dark:border-purple-700/60 shadow-md hover:shadow-lg transition-all duration-300">
                            <Text
                              variant="p"
                              size="xs"
                              className="text-purple-600 dark:text-purple-400 font-bold mb-2 uppercase tracking-wide"
                            >
                              Sosyal-Duygusal Öğrenme
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-purple-800 dark:text-purple-200"
                            >
                              {selectedMaarifData.socialEmotionalLearning.join(
                                ", ",
                              )}
                            </Text>
                          </div>
                        )}
                      {selectedMaarifData.literacySkills &&
                        selectedMaarifData.literacySkills.length > 0 && (
                          <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200/60 dark:border-green-700/60 shadow-md hover:shadow-lg transition-all duration-300">
                            <Text
                              variant="p"
                              size="xs"
                              className="text-green-600 dark:text-green-400 font-bold mb-2 uppercase tracking-wide"
                            >
                              Okuryazarlık Becerileri
                            </Text>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-green-800 dark:text-green-200"
                            >
                              {selectedMaarifData.literacySkills.join(", ")}
                            </Text>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {learningOutcomes.length === 0 &&
                  !selectedMaarifData.contentFramework &&
                  !selectedMaarifData.keyConcepts && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg
                          className="w-8 h-8 text-blue-400 dark:text-blue-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                          />
                        </svg>
                      </div>
                      <Text
                        variant="p"
                        className="text-blue-500 dark:text-blue-400 font-medium"
                      >
                        Henüz seçilmiş Maarif verileri bulunmuyor...
                      </Text>
                      <Text
                        variant="p"
                        size="sm"
                        className="text-blue-400 dark:text-blue-500 mt-1"
                      >
                        İlk iki adımdan seçilen veriler burada görüntülenecek.
                      </Text>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Previous Goals Section - Side by Side */}
      {(ubdUnderstandingGoals.some((g) => g.trim()) ||
        ubdTransferGoals.some((g) => g.trim())) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Transfer Goals */}
          {ubdTransferGoals.some((g) => g.trim()) && (
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-teal-400/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-300" />
              <div className="relative bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-900/20 dark:via-teal-900/20 dark:to-cyan-900/20 rounded-3xl p-8 border border-emerald-200/60 dark:border-emerald-700/60 shadow-xl backdrop-blur-sm">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-3 bg-white/90 dark:bg-emerald-900/50 px-6 py-3 rounded-2xl shadow-lg backdrop-blur-sm border border-emerald-200/50 dark:border-emerald-600/50">
                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-md">
                      <Text
                        variant="h4"
                        className="text-white font-bold text-sm"
                      >
                        T
                      </Text>
                    </div>
                    <Text
                      variant="h3"
                      className="text-lg font-bold bg-gradient-to-r from-emerald-700 to-teal-700 dark:from-emerald-200 dark:to-teal-200 bg-clip-text text-transparent"
                    >
                      Transfer Hedefleri
                    </Text>
                  </div>
                </div>

                <div className="space-y-4">
                  {ubdTransferGoals.map(
                    (goal, index) =>
                      goal.trim() && (
                        <div
                          key={index}
                          className="bg-white/95 dark:bg-emerald-900/30 rounded-2xl p-5 border border-emerald-200/50 dark:border-emerald-700/50 shadow-lg backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md flex-shrink-0 mt-0.5">
                              <Text
                                variant="p"
                                size="xs"
                                className="text-white font-bold"
                              >
                                T{index + 1}
                              </Text>
                            </div>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-emerald-800 dark:text-emerald-200 leading-relaxed"
                            >
                              {goal}
                            </Text>
                          </div>
                        </div>
                      ),
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Understanding Goals */}
          {ubdUnderstandingGoals.some((g) => g.trim()) && (
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-400/20 to-purple-400/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-300" />
              <div className="relative bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 dark:from-violet-900/20 dark:via-purple-900/20 dark:to-indigo-900/20 rounded-3xl p-8 border border-violet-200/60 dark:border-violet-700/60 shadow-xl backdrop-blur-sm">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-3 bg-white/90 dark:bg-violet-900/50 px-6 py-3 rounded-2xl shadow-lg backdrop-blur-sm border border-violet-200/50 dark:border-violet-600/50">
                    <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                      <Text
                        variant="h4"
                        className="text-white font-bold text-sm"
                      >
                        A
                      </Text>
                    </div>
                    <Text
                      variant="h3"
                      className="text-lg font-bold bg-gradient-to-r from-violet-700 to-purple-700 dark:from-violet-200 dark:to-purple-200 bg-clip-text text-transparent"
                    >
                      Anlama Hedefleri
                    </Text>
                  </div>
                </div>

                <div className="space-y-4">
                  {ubdUnderstandingGoals.map(
                    (goal, index) =>
                      goal.trim() && (
                        <div
                          key={index}
                          className="bg-white/95 dark:bg-violet-900/30 rounded-2xl p-5 border border-violet-200/50 dark:border-violet-700/50 shadow-lg backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-6 h-6 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md flex-shrink-0 mt-0.5">
                              <Text
                                variant="p"
                                size="xs"
                                className="text-white font-bold"
                              >
                                A{index + 1}
                              </Text>
                            </div>
                            <Text
                              variant="p"
                              size="sm"
                              className="text-violet-800 dark:text-violet-200 leading-relaxed"
                            >
                              {goal}
                            </Text>
                          </div>
                        </div>
                      ),
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* UBD Assessment Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-700 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-r from-orange-500 to-amber-600 rounded-xl shadow-md hover:scale-110 transition-transform duration-200">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <div>
            <Text
              variant="h2"
              className="text-lg font-bold text-gray-900 dark:text-gray-100"
            >
              UBD Değerlendirme & Öğrenme Deneyimleri
            </Text>
            <Text
              variant="p"
              size="sm"
              className="text-gray-600 dark:text-gray-400"
            >
              AI ile değerlendirme yöntemleri ve öğrenme etkinlikleri oluşturun
            </Text>
          </div>
        </div>

        <div className="relative">
          <Button
            variant="solid"
            onClick={handleGenerateAll}
            disabled={isAnyLoading || !planId}
            className="generate-all-button px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white text-sm rounded-xl transition-all duration-300 disabled:opacity-50 hover:scale-105 transform hover:shadow-xl font-semibold"
          >
            {isAnyLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-3"></div>
                <span className="animate-pulse">Tümü Oluşturuluyor...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-3 animate-pulse" />
                Tümünü AI ile Oluştur
              </>
            )}
          </Button>

          {/* Warning Popover */}
          {showWarningPopover && (
            <div className="warning-popover absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50 animate-fade-in">
              {/* Triangle pointer */}
              <div className="absolute -top-2 right-6 w-4 h-4 bg-white dark:bg-gray-800 border-l border-t border-gray-200 dark:border-gray-700 transform rotate-45"></div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
                </div>
                <div className="flex-1">
                  <Text
                    variant="p"
                    className="text-gray-800 dark:text-gray-200 font-medium text-sm mb-2"
                  >
                    Mevcut İçeriği Değiştir
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-400 text-xs mb-1"
                  >
                    Değerlendirme ve öğrenme deneyimleri bölümlerinde mevcut
                    içerik bulunuyor.
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-400 text-xs mb-3"
                  >
                    AI ile yeni içerik oluşturduğunuzda,{" "}
                    <strong>tüm mevcut içerikleriniz silinecek</strong> ve
                    yerine AI tarafından oluşturulan yeni içerik gelecektir.
                  </Text>
                  <div className="flex space-x-2">
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={() => setShowWarningPopover(false)}
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-xs rounded-md"
                    >
                      İptal
                    </Button>
                    <Button
                      variant="solid"
                      size="sm"
                      onClick={confirmGenerateAll}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded-md"
                    >
                      Evet, Değiştir
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {planId ? (
        <>
          {/* UBD Assessment Design Guidelines */}
          <div className="bg-gradient-to-r from-blue-50/80 via-indigo-50/80 to-purple-50/80 dark:from-blue-900/20 dark:via-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-blue-200/50 dark:border-blue-700/50 shadow-lg backdrop-blur-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <Text
                variant="h3"
                className="text-xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 dark:from-blue-200 dark:to-indigo-200 bg-clip-text text-transparent"
              >
                UbD Değerlendirme Tasarımı İçin İpuçları
              </Text>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Performance Tasks Guidelines */}
              <div className="bg-white/90 dark:bg-purple-900/20 rounded-xl p-5 border border-purple-200/50 dark:border-purple-700/50 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center shadow-md">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                      />
                    </svg>
                  </div>
                  <Text
                    variant="h4"
                    className="text-lg font-bold text-purple-800 dark:text-purple-200"
                  >
                    Performans Görevleri
                  </Text>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-purple-700 dark:text-purple-300 leading-relaxed"
                    >
                      Gerçek yaşam problemi, bağlamı ve sorunları içermeli
                    </Text>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-purple-700 dark:text-purple-300 leading-relaxed"
                    >
                      Sadece ürüne değil, aynı zamanda sürece de yayılmalı
                    </Text>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-purple-700 dark:text-purple-300 leading-relaxed"
                    >
                      Anlamanın altı yüzünden bir ya da birkaçını içermeli
                    </Text>
                  </div>
                </div>
              </div>

              {/* Other Evidence Guidelines */}
              <div className="bg-white/90 dark:bg-emerald-900/20 rounded-xl p-5 border border-emerald-200/50 dark:border-emerald-700/50 shadow-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <Text
                    variant="h4"
                    className="text-lg font-bold text-emerald-800 dark:text-emerald-200"
                  >
                    Diğer Kanıtlar
                  </Text>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-emerald-700 dark:text-emerald-300 leading-relaxed"
                    >
                      Temel hedeflere (bilgi, beceri vb.) odaklanmalı
                    </Text>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-emerald-700 dark:text-emerald-300 leading-relaxed"
                    >
                      <strong>Örnek:</strong> Gözlem, öz değerlendirme, akran
                      değerlendirme, quiz, test, kontrol listesi, günlük,
                      portfolyo, yansıtma yazıları
                    </Text>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                    <Text
                      variant="p"
                      size="sm"
                      className="text-emerald-700 dark:text-emerald-300 leading-relaxed"
                    >
                      Performans göreviyle ölçülmeyen her hedef için uygun kanıt
                      toplanmalı
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2x2 Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Performance Tasks - Purple Character */}
            {renderSectionCards(
              performanceTasks,
              updatePerformanceTask,
              addPerformanceTask,
              removePerformanceTask,
              generatePerformanceTasksAI,
              {
                title: "Performans Görevleri",
                icon: (
                  <ClipboardCheck className="w-4 h-4 text-purple-700 dark:text-purple-300" />
                ),
                placeholder: "Role play, proje, senaryo analizi...",
                description:
                  "Gerçek hayat durumlarında anlama ve transferi gösteren kompleks görevler",
                isLoading: loadingStates.performanceTasks,
                colorClasses: {
                  iconColor:
                    "bg-gradient-to-br from-purple-100 to-violet-200 dark:from-purple-900/40 dark:to-violet-800/40 border border-purple-300 dark:border-purple-600",
                  cardBg:
                    "bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/15 dark:to-violet-900/15 border-2",
                  cardBorder:
                    "border-purple-400 dark:border-purple-500 hover:border-purple-500 dark:hover:border-purple-400",
                  focusRing:
                    "focus:border-purple-600 dark:focus:border-purple-300 focus:ring-4 focus:ring-purple-500/30 dark:focus:ring-purple-400/30",
                  aiButton:
                    "bg-gradient-to-r from-purple-600 via-purple-700 to-violet-700 hover:from-purple-700 hover:via-purple-800 hover:to-violet-800 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40",
                  addButtonHover:
                    "hover:bg-gradient-to-br hover:from-purple-100 hover:to-violet-100 dark:hover:from-purple-900/30 dark:hover:to-violet-900/30 hover:border-purple-500",
                },
              },
            )}

            {/* Other Evidence - Teal Character */}
            {renderSectionCards(
              otherEvidence,
              updateOtherEvidence,
              addOtherEvidence,
              removeOtherEvidence,
              generateOtherEvidenceAI,
              {
                title: "Diğer Kanıtlar",
                icon: (
                  <Eye className="w-4 h-4 text-teal-700 dark:text-teal-300" />
                ),
                placeholder: "Test, form, gözlem, yazı...",
                description:
                  "Testler, kısa cevaplar, gözlemler ve akademik değerlendirmeler",
                isLoading: loadingStates.otherEvidence,
                colorClasses: {
                  iconColor:
                    "bg-gradient-to-br from-teal-100 to-cyan-200 dark:from-teal-900/40 dark:to-cyan-800/40 border border-teal-300 dark:border-teal-600",
                  cardBg:
                    "bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/15 dark:to-cyan-900/15 border-2",
                  cardBorder:
                    "border-teal-400 dark:border-teal-500 hover:border-teal-500 dark:hover:border-teal-400",
                  focusRing:
                    "focus:border-teal-600 dark:focus:border-teal-300 focus:ring-4 focus:ring-teal-500/30 dark:focus:ring-teal-400/30",
                  aiButton:
                    "bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-700 hover:from-teal-700 hover:via-teal-800 hover:to-cyan-800 shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40",
                  addButtonHover:
                    "hover:bg-gradient-to-br hover:from-teal-100 hover:to-cyan-100 dark:hover:from-teal-900/30 dark:hover:to-cyan-900/30 hover:border-teal-500",
                },
              },
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🔒</div>
          <Text
            variant="h3"
            className="text-xl font-bold text-gray-600 dark:text-gray-400 mb-2"
          >
            Plan Oluşturma Gerekli
          </Text>
          <Text variant="p" className="text-gray-500 dark:text-gray-400">
            Değerlendirme yöntemlerinizi oluşturmak için önce "Programlar Arası
            Bileşenler" adımından plan oluşturmanız gerekir.
          </Text>
        </div>
      )}

      {/* Updated Tips Section */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4 hover:shadow-md transition-all duration-300">
        <div className="flex items-center space-x-2 mb-3">
          <Lightbulb className="w-4 h-4 text-yellow-500 animate-pulse" />
          <Text
            variant="h4"
            className="text-sm font-semibold text-gray-800 dark:text-gray-200"
          >
            UBD Değerlendirme İpuçları
          </Text>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          {/* Performance Tasks */}
          <div className="space-y-1 bg-orange-50/70 dark:bg-orange-900/10 rounded-lg p-3 border border-orange-200 dark:border-orange-700">
            <Text
              variant="h5"
              className="text-xs font-bold text-orange-800 dark:text-orange-200 mb-1"
            >
              Performans Görevleri
            </Text>
            <ul className="space-y-1 text-orange-700 dark:text-orange-300">
              <li>• Gerçek yaşam bağlamında uygulanabilir olmalı</li>
              <li>
                • Öğrencilerin öğrendiklerini göstermelerine olanak sağlamalı
              </li>
              <li>• Çoklu değerlendirme kriteri içermeli</li>
            </ul>
          </div>

          {/* Other Evidence */}
          <div className="space-y-1 bg-emerald-50/70 dark:bg-emerald-900/10 rounded-lg p-3 border border-emerald-200 dark:border-emerald-700">
            <Text
              variant="h5"
              className="text-xs font-bold text-emerald-800 dark:text-emerald-200 mb-1"
            >
              Diğer Kanıtlar
            </Text>
            <ul className="space-y-1 text-emerald-700 dark:text-emerald-300">
              <li>• Gözlem, öz değerlendirme, akran değerlendirme</li>
              <li>• Quiz, test, kontrol listesi</li>
              <li>• Günlük, portfolyo, yansıtma yazıları</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UbdAssessmentExperiencesStep;
